import React from 'react';
import {
  Card,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Button, Switch, Typography } from '@thingsw/pitta-design-system';

import { SettingSlider } from '../SettingSlider';
import clsx from 'clsx';
import {
  Webviewer,
  LightColors,
  ILiveEventSettings,
  TabNameInfo3,
  OnOffType,
  TrueFalsetype,
  BlackvueLightColors,
} from '@thingsw/pitta-modules';
import { CAMERA } from '../../features/Camera/slice';
import { useSelector } from 'react-redux';
import { RootState } from '../../features/store';
import ableExtendBoxPro from '../../utils/ableExtendBoxPro';
import { THEME } from '../../features/Theme/slice';
import useEliteModel from '../../hooks/useEliteModel';

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    margin: '0 0 7px 21px',
  },

  card: {
    maxWidth: 673,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 12,
    boxShadow:
      '0px 2px 2px rgba(0, 0, 0, 0.06), 0px 6px 40px rgba(0, 0, 0, 0.05)',
  },

  switch: {
    marginRight: 19,
    height: 38,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      marginRight: 12,
      width: 48,
    },
  },

  boderBottomBox: {
    width: '100%',
    maxWidth: 672,
    minHeight: 48,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 15,
    borderBottom: `1px solid ${LightColors.primary['5']}`,
  },

  boderBottomNoneBox: {
    width: '100%',
    maxWidth: 672,
    // minHeight: 48,
    display: 'flex',
    alignItems: 'center',
    padding: '12px 20px 9px 24px',
  },

  boderNoneTwoRowBox: {
    width: '100%',
    maxWidth: 672,
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px 9px 24px',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      padding: '12px 20px 9px 24px',
    },
  },

  boderNoneThirdPadding: {
    padding: '5px 20px 5px 24px',
  },

  hardAccelerationPadding: {
    padding: '13px 20px 3px 24px',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      padding: '13px 20px 6px 24px',
    },
  },

  thresholdPadding: {
    padding: '6px 19px 0px 19px',
    flexDirection: 'column',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      padding: '6px 20px 2px 20px',
      flexDirection: 'row',
    },
  },

  liveEventUploadPadding: {
    padding: '8px 20px 9px 24px',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      padding: '9px 20px 9px 24px',
    },
  },

  beepAlertDiv: {
    padding: '5px 20px 4px 24px',
  },

  thresholdDiv: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      width: '50%',
      paddingRight: 27,
    },
  },

  thresholdInfo: {
    padding: '1px 19px 2px 20px',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      padding: '1px 20px 2px 20px',
    },
  },

  textColume: {
    display: 'flex',
    flexDirection: 'column',
  },

  cardDivider: {
    margin: '15px 0',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      maxWidth: 671,
    },
  },
  contentDivider: {
    margin: '8px 0px 0px',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      margin: '8px 20px 0px',
    },
  },
  sensitivityDiv: {
    margin: '12px 20px 0',
  },
  sliderOptionWrap: {
    display: 'flex',
    padding: '10px 20px',
    justifyContent: 'space-between',
  },
  marginR16: {
    marginRight: 16,
  },
  protectEventDiv: {
    display: 'flex',
    flexDirection: 'row',
    padding: '16px 20px',
  },
  protectInfoText: {
    display: 'flex',
    flexDirection: 'column',
  },
  switchDiv: {
    display: 'flex',
  },

  selectOptionWrap: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 48,
    padding: '16px 20px',
    width: '100%',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      maxWidth: 672,
      alignItems: 'center',
      flexDirection: 'row',
      padding: '12px 20px',
    },
  },
  speedAlertDiv: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'inherit',
    padding: '21px 20px 9px 20px',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      justifyContent: 'space-between',
      flexDirection: 'row',
      alignItems: 'center',
      padding: '12px 20px 9px 24px',
    },
  },

  speedAlertSwitchDiv: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 4,
    [theme.breakpoints.up(Webviewer.mobile)]: {
      paddingLeft: 0,
    },
  },

  spaceBetween: {
    justifyContent: 'center',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      justifyContent: 'space-between',
    },
  },
  selectDiv: {
    paddingTop: 16,
    width: '100%',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      width: 'auto',
      minWidth: 267,
      padding: '0',
    },
  },
  select: {
    borderRadius: 12,
    padding: '8px 16px',
  },
  selectPaper: {
    width: 234,
  },

  inputDiv: {
    minWidth: 303,
    marginTop: 20,
    [theme.breakpoints.up(Webviewer.mobile)]: {
      minWidth: 267,
      width: 267,
      marginTop: 0,
    },
  },
  input: {
    borderRadius: 12,
    padding: '8px 16px',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      padding: '8px 24px',
    },
  },
  sliderDiv: {
    width: '100%',
    marginRight: 12,
    [theme.breakpoints.up(Webviewer.mobile)]: {
      width: '50%',
      marginRight: 0,
    },
  },
  sliderRoot: {
    padding: '16px 0',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      padding: '20px 0',
    },
  },
  sliderInnerDiv: {
    paddingLeft: 11,
    [theme.breakpoints.up(Webviewer.mobile)]: {
      paddingLeft: 13,
    },
  },
  disabledText: {
    opacity: 0.35,
  },
  sliderRail: {
    color: LightColors.primary['1'],
  },
  trackRail: {
    color: LightColors.primary['3'],
    opacity: 0.38,
  },
}));

interface ManualRecordingPanelProps {
  currentLiveEvent?: ILiveEventSettings;
  currentTab3Obj?: TabNameInfo3;
  onChangeTab3Setting: (key: keyof TabNameInfo3, val: string | boolean) => void;
  onLiveEventUpload: (key: keyof ILiveEventSettings, val: boolean) => void;
  liveUploadLoading?: boolean;
}

export const ManualRecordingPanel = ({
  currentLiveEvent,
  currentTab3Obj,
  onChangeTab3Setting,
  onLiveEventUpload,
  liveUploadLoading,
}: ManualRecordingPanelProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));
  const { color, colors } = useSelector((state: RootState) => state[THEME]);

  const { t } = useTranslation();

  const { camera } = useSelector((state: RootState) => state[CAMERA]);

  const { isEliteModel } = useEliteModel(camera);

  const isBoxPro = ableExtendBoxPro(camera?.model, camera?.fw_ver);

  const calcMPH = (unit: string, speed: string, defaultValue: string) => {
    if (speed === '0') {
      return defaultValue;
    }
    //mph
    if (unit === '1') {
      return speed;
    }
    // kmh
    return (parseFloat(speed) * 0.6213).toFixed(0);
  };

  const calcKMH = (unit: string, speed: string, defaultValue: string) => {
    if (speed === '0') {
      return defaultValue;
    }
    //mph
    if (unit === '1') {
      return (parseFloat(speed) * 1.6093).toFixed(0);
    }
    // kmh
    return speed;
  };

  const getBooleanOnOFF = (onOff?: OnOffType) => {
    return onOff === 'on' ? true : false;
  };

  const getBoolean = (TrueFalse: TrueFalsetype) => {
    return TrueFalse === '1' ? true : false;
  };

  return (
    <>
      <div className={classes.title}>
        <Typography category='Default' variant='SmallBold'>
          {t('Manual recording')}
        </Typography>
      </div>

      <Card variant='outlined' className={classes.card}>
        <div
          className={clsx(
            classes.boderBottomBox,
            classes.hardAccelerationPadding
          )}
        >
          <Typography category='Default' variant='Body'>
            {t('Recording trigger')}
          </Typography>
        </div>

        <div className={classes.boderNoneTwoRowBox}>
          <div className={classes.switch}>
            <Switch
              darkMode={color === 'dark'}
              checked={getBoolean(currentTab3Obj?.['PSENSOR'] as TrueFalsetype)}
              onChange={(e) => {
                onChangeTab3Setting('PSENSOR', e.target.checked);
              }}
            />
          </div>
          <div className={classes.textColume}>
            <Typography category='Default' variant='Body'>
              {t('Touch sensor')}
            </Typography>
          </div>
        </div>

        <div
          style={{
            gap: 14,
            justifyContent: 'space-between',
          }}
          className={classes.boderNoneTwoRowBox}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography category='Default' variant='Body'>
              {t('Remote Button (Optional)')}
            </Typography>
            <Typography
              htmlColor={LightColors.primary['2']}
              category='Default'
              variant='Caption'
            >
              {t('Remote button settings_')}
            </Typography>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 16,
              alignItems: 'center',
            }}
          >
            {currentTab3Obj?.['EBRFID'] ? (
              <Typography
                htmlColor={LightColors.primary['4']}
                category='Default'
                variant='SmallBold'
                style={{ minWidth: 110 }}
              >
                {currentTab3Obj?.['EBRFID']}
              </Typography>
            ) : (
              <Typography
                htmlColor={LightColors.primary['4']}
                category='Default'
                variant='SmallBold'
                style={{ minWidth: 140 }}
              >
                {t('No Registered Device')}
              </Typography>
            )}

            <Button
              variant='contained'
              color='primary'
              disabled={true}
              style={{
                height: 36,
                padding: '12px 16px',
                ...(currentTab3Obj?.['EBRFID']
                  ? {
                      minWidth: 130,
                      backgroundColor: colors.primary['0'],
                      color: colors.primary['4'],
                      border: `1px solid ${colors.primary['4']}`,
                      padding: 12,
                    }
                  : {}),
              }}
              endIcon={
                <svg
                  width='6'
                  height='10'
                  viewBox='0 0 6 10'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  style={currentTab3Obj?.['EBRFID'] ? { marginLeft: 12 } : {}}
                >
                  <path
                    d='M1.48828 0.5L5.98828 5L1.48828 9.5L0.433594 8.44531L3.87891 5L0.433594 1.55469L1.48828 0.5Z'
                    fill={
                      currentTab3Obj?.['EBRFID'] ? colors.primary['4'] : 'white'
                    }
                  />
                </svg>
              }
            >
              {currentTab3Obj?.['EBRFID'] ? t('Re-register') : t('Register')}
            </Button>
          </div>
        </div>

        <div
          className={clsx(
            classes.boderBottomNoneBox,
            classes.boderNoneThirdPadding
          )}
        >
          <div className={classes.switch}>
            <Switch
              darkMode={color === 'dark'}
              disabled={
                currentTab3Obj?.['PSENSOR'] === '0' &&
                !currentTab3Obj?.['EBRFID']
              }
              checked={
                currentTab3Obj?.['PSENSOR'] === '0' &&
                !currentTab3Obj?.['EBRFID']
                  ? false
                  : currentLiveEvent?.ManualRecording === 'on'
              }
              onChange={(e) => {
                onLiveEventUpload('ManualRecording', e.target.checked);
              }}
            />
          </div>

          <div className={classes.textColume}>
            <Typography
              category='Default'
              variant='Body'
              // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
              className={clsx(
                currentTab3Obj?.['PSENSOR'] === '0' &&
                  !currentTab3Obj?.['EBRFID'] &&
                  classes.disabledText
              )}
            >
              {t('Live Event Upload_2')}
            </Typography>
            <Typography
              category='Default'
              variant='Small'
              htmlColor={LightColors.primary['2']}
              style={{ marginTop: 2 }}
              // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
              className={clsx(
                currentTab3Obj?.['PSENSOR'] === '0' &&
                  !currentTab3Obj?.['EBRFID'] &&
                  classes.disabledText
              )}
            >
              {t('Manual Event files will_')}
            </Typography>
          </div>
        </div>
      </Card>

      <div className={classes.cardDivider} />

      {!isEliteModel && (
        <>
          {/* Hard acceleration Event card */}

          <Card variant='outlined' className={classes.card}>
            <div
              className={clsx(
                classes.boderBottomBox,
                classes.hardAccelerationPadding
              )}
            >
              <div className={classes.switch}>
                <Switch
                  darkMode={color === 'dark'}
                  // mantis - 10908, AccelLimit 0이 아니면 해당 항목 on되도록 수정 (Leehj)
                  // checked={acceleration}
                  checked={currentTab3Obj?.AccelLimit !== '0'}
                  onChange={(e) => {
                    // setAcceleration(e.target.checked);
                    if (e.target.checked) {
                      onChangeTab3Setting(
                        'AccelLimit',
                        currentTab3Obj?.EventSpeedUnit === '1' ? '7' : '11'
                      );
                      // OFF -> ON 으로 변경될때는LiveEventUpload은 OFF로 노출 (Leehj) 23.01.10
                      if (currentLiveEvent?.NerAcceleration === 'on') {
                        onLiveEventUpload('NerAcceleration', false);
                      }
                      // OFF -> ON 으로 변경될때는 Beep Alert은 기본값 OFF로 노출 (Leehj) 23.01.09
                      // onChangeTab3Setting("ACCELERATIONVOICE", "1");
                    } else {
                      // mantis - 10908, 실제로 서버에는 AccelLimit 0으로 저장하고 ui만 11로 보이도록 (Leehj)
                      onChangeTab3Setting('AccelLimit', '0');
                      onChangeTab3Setting('ACCELERATIONVOICE', '0');
                      if (currentLiveEvent?.NerAcceleration === 'on') {
                        onLiveEventUpload('NerAcceleration', false);
                      }
                    }
                  }}
                />
              </div>

              <Typography category='Default' variant='Body'>
                {/* mantis - 11122, Hard acceleration Event -> Hard Acceleration로 문구 수정 (Leehj) */}
                {t('Hard acceleration Event')}
              </Typography>
            </div>

            <div
              className={clsx(
                classes.boderBottomNoneBox,
                classes.thresholdPadding
              )}
            >
              <div className={classes.thresholdDiv}>
                <Typography
                  category='Default'
                  variant='SmallBold'
                  style={{ lineHeight: 1 }}
                  // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                  // mantis - 10908, AccelLimit 0이면 비활성화 (Leehj)
                  className={clsx(
                    currentTab3Obj?.AccelLimit === '0' && classes.disabledText
                  )}
                >
                  {t('Threshold')}
                </Typography>
                <Typography
                  category='Default'
                  variant='SmallBold'
                  htmlColor={
                    currentTab3Obj?.AccelLimit !== '0'
                      ? BlackvueLightColors.primary['7']
                      : BlackvueLightColors.primary['1']
                  }
                  style={{ lineHeight: 1 }}
                  // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                  // mantis - 10908, AccelLimit 0이면 비활성화 (Leehj)
                  className={clsx(
                    currentTab3Obj?.AccelLimit === '0' && classes.disabledText
                  )}
                >
                  {currentTab3Obj?.EventSpeedUnit === '0'
                    ? `${calcKMH(
                        currentTab3Obj?.EventSpeedUnit ?? '0',
                        currentTab3Obj?.AccelLimit ?? '0',
                        '11'
                      )}km/h (${calcMPH(
                        currentTab3Obj?.EventSpeedUnit ?? '0',
                        currentTab3Obj?.AccelLimit ?? '0',
                        '7'
                      )}MPH)`
                    : `${calcMPH(
                        currentTab3Obj?.EventSpeedUnit ?? '0',
                        currentTab3Obj?.AccelLimit ?? '0',
                        '7'
                      )}MPH (${calcKMH(
                        currentTab3Obj?.EventSpeedUnit ?? '0',
                        currentTab3Obj?.AccelLimit ?? '0',
                        '11'
                      )}km/h)`}
                </Typography>
              </div>
              <div className={classes.sliderDiv}>
                <SettingSlider
                  rootClasses={classes.sliderRoot}
                  railClasses={clsx(
                    currentTab3Obj?.AccelLimit === '0' && classes.sliderRail
                  )}
                  trackClasses={clsx(
                    currentTab3Obj?.AccelLimit === '0' && classes.trackRail
                  )}
                  sliderClasses={classes.sliderInnerDiv}
                  min={currentTab3Obj?.EventSpeedUnit === '1' ? 6 : 10}
                  max={currentTab3Obj?.EventSpeedUnit === '1' ? 37 : 60}
                  value={parseInt(
                    currentTab3Obj?.AccelLimit === undefined ||
                      currentTab3Obj?.AccelLimit === '0'
                      ? currentTab3Obj?.EventSpeedUnit === '1'
                        ? '7'
                        : '11'
                      : currentTab3Obj?.AccelLimit
                  )}
                  // mantis - 10908, AccelLimit 0이면 비활성화 (Leehj)
                  disabled={currentTab3Obj?.AccelLimit === '0'}
                  onChange={(e, value) => {
                    if (!liveUploadLoading) {
                      const v = value as number;
                      onChangeTab3Setting('AccelLimit', v.toString());
                    }
                  }}
                />
              </div>
            </div>

            <div
              className={clsx(
                classes.boderBottomNoneBox,
                classes.thresholdInfo
              )}
            >
              <Typography
                category='Default'
                variant='Caption'
                htmlColor={LightColors.primary['2']}
                style={{ fontSize: mobile ? 12 : 11 }}
                // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                className={clsx(
                  currentTab3Obj?.AccelLimit === '0' && classes.disabledText
                )}
              >
                {t('Triggers when the_')}
              </Typography>
            </div>
            <div className={classes.contentDivider} />
            <div
              className={clsx(
                classes.boderNoneTwoRowBox,
                classes.liveEventUploadPadding
              )}
            >
              <div className={classes.switch}>
                <Switch
                  darkMode={color === 'dark'}
                  // mantis - 10908, AccelLimit 0이면 비활성화 (Leehj)
                  disabled={currentTab3Obj?.AccelLimit === '0'}
                  checked={getBooleanOnOFF(
                    currentLiveEvent?.NerAcceleration ?? 'off'
                  )}
                  onChange={(event) => {
                    // 각 항목에 대해 설정을 변경하면 바로 서버로 설정값 반영한다.
                    onLiveEventUpload('NerAcceleration', event.target.checked);
                  }}
                />
              </div>
              <div className={classes.textColume}>
                <Typography
                  category='Default'
                  variant='Body'
                  // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                  className={clsx(
                    currentTab3Obj?.AccelLimit === '0' && classes.disabledText
                  )}
                >
                  {t('Live Event Upload_2')}
                </Typography>
                <Typography
                  category='Default'
                  variant='Small'
                  htmlColor={LightColors.primary['2']}
                  style={{ marginTop: 2 }}
                  // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                  className={clsx(
                    currentTab3Obj?.AccelLimit === '0' && classes.disabledText
                  )}
                >
                  {t('Event file will_')}
                </Typography>
              </div>
            </div>

            <div
              className={clsx(classes.boderBottomNoneBox, classes.beepAlertDiv)}
            >
              <div className={classes.switch}>
                <Switch
                  darkMode={color === 'dark'}
                  // mantis - 10908, AccelLimit 0이면 비활성화 (Leehj)
                  disabled={currentTab3Obj?.AccelLimit === '0'}
                  checked={getBoolean(
                    currentTab3Obj?.ACCELERATIONVOICE ?? ('0' as TrueFalsetype)
                  )}
                  onChange={(e) => {
                    onChangeTab3Setting('ACCELERATIONVOICE', e.target.checked);
                  }}
                />
              </div>

              <Typography
                category='Default'
                variant='Body'
                // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                className={clsx(
                  currentTab3Obj?.AccelLimit === '0' && classes.disabledText
                )}
              >
                {isBoxPro ? t('Voice Alert') : t('Beep Alert')}
              </Typography>
            </div>
          </Card>
          <div className={classes.cardDivider} />

          {/* Hard braking Event card */}
          <Card variant='outlined' className={classes.card}>
            <div
              className={clsx(
                classes.boderBottomBox,
                classes.hardAccelerationPadding
              )}
            >
              <div className={classes.switch}>
                <Switch
                  darkMode={color === 'dark'}
                  // mantis - 10908, HarshLimit 0이 아니면 해당 항목 on되도록 수정 (Leehj)
                  // checked={braking}
                  checked={currentTab3Obj?.HarshLimit !== '0'}
                  onChange={(e) => {
                    // setBraking(e.target.checked);
                    if (e.target.checked) {
                      onChangeTab3Setting(
                        'HarshLimit',
                        currentTab3Obj?.EventSpeedUnit === '1' ? '8' : '13'
                      );
                      // OFF -> ON 으로 변경될때는LiveEventUpload은 OFF로 노출 (Leehj) 23.01.10
                      if (currentLiveEvent?.NerHarshBraking === 'on') {
                        onLiveEventUpload('NerHarshBraking', false);
                      }
                      // OFF -> ON 으로 변경될때는 Beep Alert은 기본값 OFF로 노출 (Leehj) 23.01.09
                      // onChangeTab3Setting("HARSHBRAKINGVOICE", "1");
                    } else {
                      // mantis - 10908, 실제로 서버에는 HarshLimit 0으로 저장하고 ui만 13로 보이도록 (Leehj)
                      onChangeTab3Setting('HarshLimit', '0');
                      onChangeTab3Setting('HARSHBRAKINGVOICE', '0');
                      if (currentLiveEvent?.NerHarshBraking === 'on') {
                        onLiveEventUpload('NerHarshBraking', false);
                      }
                    }
                  }}
                />
              </div>

              <Typography category='Default' variant='Body'>
                {/* mantis - 11122, Hard braking Event -> Hard Braking로 문구 수정 (Leehj) */}
                {t('Hard braking Event')}
              </Typography>
            </div>

            <div
              className={clsx(
                classes.boderBottomNoneBox,
                classes.thresholdPadding
              )}
            >
              <div className={classes.thresholdDiv}>
                <Typography
                  category='Default'
                  variant='SmallBold'
                  style={{ lineHeight: 1 }}
                  // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                  className={clsx(
                    currentTab3Obj?.HarshLimit === '0' && classes.disabledText
                  )}
                >
                  {t('Threshold')}
                </Typography>
                <Typography
                  category='Default'
                  variant='SmallBold'
                  htmlColor={
                    currentTab3Obj?.HarshLimit !== '0'
                      ? BlackvueLightColors.primary['7']
                      : BlackvueLightColors.primary['1']
                  }
                  style={{ lineHeight: 1 }}
                  // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                  className={clsx(
                    currentTab3Obj?.HarshLimit === '0' && classes.disabledText
                  )}
                >
                  {currentTab3Obj?.EventSpeedUnit === '0'
                    ? `${calcKMH(
                        currentTab3Obj?.EventSpeedUnit ?? '0',
                        currentTab3Obj?.HarshLimit ?? '0',
                        '13'
                      )}km/h (${calcMPH(
                        currentTab3Obj?.EventSpeedUnit ?? '0',
                        currentTab3Obj?.HarshLimit ?? '0',
                        '8'
                      )}MPH)`
                    : `${calcMPH(
                        currentTab3Obj?.EventSpeedUnit ?? '0',
                        currentTab3Obj?.HarshLimit ?? '0',
                        '8'
                      )}MPH (${calcKMH(
                        currentTab3Obj?.EventSpeedUnit ?? '0',
                        currentTab3Obj?.HarshLimit ?? '0',
                        '13'
                      )}km/h)`}
                </Typography>
              </div>

              <div className={classes.sliderDiv}>
                <SettingSlider
                  rootClasses={classes.sliderRoot}
                  sliderClasses={classes.sliderInnerDiv}
                  railClasses={clsx(
                    currentTab3Obj?.HarshLimit === '0' && classes.sliderRail
                  )}
                  trackClasses={clsx(
                    currentTab3Obj?.HarshLimit === '0' && classes.trackRail
                  )}
                  min={currentTab3Obj?.EventSpeedUnit === '1' ? 6 : 10}
                  max={currentTab3Obj?.EventSpeedUnit === '1' ? 31 : 50}
                  value={parseInt(
                    currentTab3Obj?.HarshLimit === undefined ||
                      currentTab3Obj?.HarshLimit === '0'
                      ? currentTab3Obj?.EventSpeedUnit === '1'
                        ? '8'
                        : '13'
                      : currentTab3Obj.HarshLimit
                  )}
                  disabled={currentTab3Obj?.HarshLimit === '0'}
                  onChange={(e, value) => {
                    if (!liveUploadLoading) {
                      const v = value as number;
                      onChangeTab3Setting('HarshLimit', v.toString());
                    }
                  }}
                />
              </div>
            </div>

            <div
              className={clsx(
                classes.boderBottomNoneBox,
                classes.thresholdInfo
              )}
            >
              <Typography
                category='Default'
                variant='Caption'
                htmlColor={LightColors.primary['2']}
                style={{ fontSize: mobile ? 12 : 11 }}
                // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                className={clsx(
                  currentTab3Obj?.HarshLimit === '0' && classes.disabledText
                )}
              >
                {t('Triggers when the_2')}
              </Typography>
            </div>
            <div className={classes.contentDivider} />
            <div
              className={clsx(
                classes.boderNoneTwoRowBox,
                classes.liveEventUploadPadding
              )}
            >
              <div className={classes.switch}>
                <Switch
                  darkMode={color === 'dark'}
                  disabled={currentTab3Obj?.HarshLimit === '0'}
                  checked={getBooleanOnOFF(
                    currentLiveEvent?.NerHarshBraking ?? 'off'
                  )}
                  onChange={(event) => {
                    // 각 항목에 대해 설정을 변경하면 바로 서버로 설정값 반영한다.
                    onLiveEventUpload('NerHarshBraking', event.target.checked);
                  }}
                />
              </div>
              <div className={classes.textColume}>
                <Typography
                  category='Default'
                  variant='Body'
                  // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                  className={clsx(
                    currentTab3Obj?.HarshLimit === '0' && classes.disabledText
                  )}
                >
                  {t('Live Event Upload_2')}
                </Typography>
                <Typography
                  category='Default'
                  variant='Small'
                  htmlColor={LightColors.primary['2']}
                  style={{ marginTop: 2 }}
                  // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                  className={clsx(
                    currentTab3Obj?.HarshLimit === '0' && classes.disabledText
                  )}
                >
                  {t('Event file will_')}
                </Typography>
              </div>
            </div>

            <div
              className={clsx(classes.boderBottomNoneBox, classes.beepAlertDiv)}
            >
              <div className={classes.switch}>
                <Switch
                  darkMode={color === 'dark'}
                  disabled={currentTab3Obj?.HarshLimit === '0'}
                  checked={getBoolean(
                    currentTab3Obj?.HARSHBRAKINGVOICE ?? ('0' as TrueFalsetype)
                  )}
                  onChange={(e) => {
                    onChangeTab3Setting('HARSHBRAKINGVOICE', e.target.checked);
                  }}
                />
              </div>

              <Typography
                category='Default'
                variant='Body'
                // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                className={clsx(
                  currentTab3Obj?.HarshLimit === '0' && classes.disabledText
                )}
              >
                {isBoxPro ? t('Voice Alert') : t('Beep Alert')}
              </Typography>
            </div>
          </Card>
          <div className={classes.cardDivider} />

          {/* Hard cornering Event card */}
          <Card variant='outlined' className={classes.card}>
            <div
              className={clsx(
                classes.boderBottomBox,
                classes.hardAccelerationPadding
              )}
            >
              <div className={classes.switch}>
                <Switch
                  darkMode={color === 'dark'}
                  // mantis - 10908, SharpLimit 0이 아니면 해당 항목 on되도록 수정 (Leehj)
                  // checked={cornering}
                  checked={currentTab3Obj?.SharpLimit !== '0'}
                  onChange={(e) => {
                    // setCornering(e.target.checked);
                    if (e.target.checked) {
                      onChangeTab3Setting(
                        'SharpLimit',
                        currentTab3Obj?.EventSpeedUnit === '1' ? '9' : '15'
                      );
                      // OFF -> ON 으로 변경될때는LiveEventUpload은 OFF로 노출 (Leehj) 23.01.10
                      if (currentLiveEvent?.NerSharpTurn === 'on') {
                        onLiveEventUpload('NerSharpTurn', false);
                      }
                      // OFF -> ON 으로 변경될때는 Beep Alert은 기본값 OFF로 노출 (Leehj) 23.01.09
                      // onChangeTab3Setting("SHARPTURNVOICE", "1");
                    } else {
                      // mantis - 10908, 실제로 서버에는 SharpLimit 0으로 저장하고 ui만 15로 보이도록 (Leehj)
                      onChangeTab3Setting('SharpLimit', '0');
                      onChangeTab3Setting('SHARPTURNVOICE', '0');
                      if (currentLiveEvent?.NerSharpTurn === 'on') {
                        onLiveEventUpload('NerSharpTurn', false);
                      }
                    }
                  }}
                />
              </div>

              <Typography category='Default' variant='Body'>
                {/* mantis - 11122, Hard cornering Event -> Sharp turn로 문구 수정 (Leehj) */}
                {t('Hard Cornering Event')}
              </Typography>
            </div>

            <div
              className={clsx(
                classes.boderBottomNoneBox,
                classes.thresholdPadding
              )}
            >
              <div className={classes.thresholdDiv}>
                <Typography
                  category='Default'
                  variant='SmallBold'
                  style={{ lineHeight: 1 }}
                  // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                  className={clsx(
                    currentTab3Obj?.SharpLimit === '0' && classes.disabledText
                  )}
                >
                  {t('Threshold')}
                </Typography>
                <Typography
                  category='Default'
                  variant='SmallBold'
                  htmlColor={
                    currentTab3Obj?.SharpLimit !== '0'
                      ? BlackvueLightColors.primary['7']
                      : BlackvueLightColors.primary['1']
                  }
                  style={{ lineHeight: 1 }}
                  // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                  className={clsx(
                    currentTab3Obj?.SharpLimit === '0' && classes.disabledText
                  )}
                >
                  {currentTab3Obj?.EventSpeedUnit === '0'
                    ? `${calcKMH(
                        currentTab3Obj?.EventSpeedUnit ?? '0',
                        currentTab3Obj?.SharpLimit ?? '0',
                        '15'
                      )}km/h (${calcMPH(
                        currentTab3Obj?.EventSpeedUnit ?? '0',
                        currentTab3Obj?.SharpLimit ?? '0',
                        '9'
                      )}MPH)`
                    : `${calcMPH(
                        currentTab3Obj?.EventSpeedUnit ?? '0',
                        currentTab3Obj?.SharpLimit ?? '0',
                        '9'
                      )}MPH (${calcKMH(
                        currentTab3Obj?.EventSpeedUnit ?? '0',
                        currentTab3Obj?.SharpLimit ?? '0',
                        '15'
                      )}km/h)`}
                </Typography>
              </div>
              <div className={classes.sliderDiv}>
                <SettingSlider
                  rootClasses={classes.sliderRoot}
                  sliderClasses={classes.sliderInnerDiv}
                  railClasses={clsx(
                    currentTab3Obj?.SharpLimit === '0' && classes.sliderRail
                  )}
                  trackClasses={clsx(
                    currentTab3Obj?.SharpLimit === '0' && classes.trackRail
                  )}
                  min={currentTab3Obj?.EventSpeedUnit === '1' ? 6 : 10}
                  max={currentTab3Obj?.EventSpeedUnit === '1' ? 31 : 50}
                  value={parseInt(
                    currentTab3Obj?.SharpLimit === undefined ||
                      currentTab3Obj?.SharpLimit === '0'
                      ? currentTab3Obj?.EventSpeedUnit === '1'
                        ? '9'
                        : '15'
                      : currentTab3Obj?.SharpLimit
                  )}
                  disabled={currentTab3Obj?.SharpLimit === '0'}
                  onChange={(e, value) => {
                    if (!liveUploadLoading) {
                      const v = value as number;
                      onChangeTab3Setting('SharpLimit', v.toString());
                    }
                  }}
                />
              </div>
            </div>

            <div
              className={clsx(
                classes.boderBottomNoneBox,
                classes.thresholdInfo
              )}
            >
              <Typography
                category='Default'
                variant='Caption'
                htmlColor={LightColors.primary['2']}
                style={{ fontSize: mobile ? 12 : 11 }}
                // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                className={clsx(
                  currentTab3Obj?.SharpLimit === '0' && classes.disabledText
                )}
              >
                {t('Triggers when more_')}
              </Typography>
            </div>
            <div className={classes.contentDivider} />
            <div
              className={clsx(
                classes.boderNoneTwoRowBox,
                classes.liveEventUploadPadding
              )}
            >
              <div className={classes.switch}>
                <Switch
                  darkMode={color === 'dark'}
                  disabled={currentTab3Obj?.SharpLimit === '0'}
                  checked={getBooleanOnOFF(
                    currentLiveEvent?.NerSharpTurn ?? 'off'
                  )}
                  onChange={(event) => {
                    // 각 항목에 대해 설정을 변경하면 바로 서버로 설정값 반영한다.
                    onLiveEventUpload('NerSharpTurn', event.target.checked);
                  }}
                />
              </div>
              <div className={classes.textColume}>
                <Typography
                  category='Default'
                  variant='Body'
                  // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                  className={clsx(
                    currentTab3Obj?.SharpLimit === '0' && classes.disabledText
                  )}
                >
                  {t('Live Event Upload_2')}
                </Typography>
                <Typography
                  category='Default'
                  variant='Small'
                  htmlColor={LightColors.primary['2']}
                  style={{ marginTop: 2 }}
                  // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                  className={clsx(
                    currentTab3Obj?.SharpLimit === '0' && classes.disabledText
                  )}
                >
                  {t('Event file will_')}
                </Typography>
              </div>
            </div>

            <div
              className={clsx(classes.boderBottomNoneBox, classes.beepAlertDiv)}
            >
              <div className={classes.switch}>
                <Switch
                  darkMode={color === 'dark'}
                  disabled={currentTab3Obj?.SharpLimit === '0'}
                  checked={getBoolean(
                    currentTab3Obj?.SHARPTURNVOICE ?? ('0' as TrueFalsetype)
                  )}
                  onChange={(e) => {
                    onChangeTab3Setting('SHARPTURNVOICE', e.target.checked);
                  }}
                />
              </div>

              <Typography
                category='Default'
                variant='Body'
                // 비활성화인 경우 텍스트도 비활성화 (Leehj) 23.01.12
                className={clsx(
                  currentTab3Obj?.SharpLimit === '0' && classes.disabledText
                )}
              >
                {isBoxPro ? t('Voice Alert') : t('Beep Alert')}
              </Typography>
            </div>
          </Card>

          <div className={classes.cardDivider} />
        </>
      )}
    </>
  );
};
